<template>
  <WModal
    v-model="isOpen"
    head-line="Machine settings"
    :action-type="Actions.CancelConfirm"
    confirm-text="Save settings"
    :confirm-disabled="!canSave"
    :allow-icon="false"
    max-width="520px"
    :section-tag="sectionTag"
    @confirm="handleConfirm"
    @cancel="isOpen = false"
  >
    <template #content>
      <div class="fleet-runner-settings">
        <p class="fleet-runner-settings__text">{{ text.message }}</p>
        <p class="fleet-runner-settings__fact" :data-testid="`${sectionTag}-run-while`">
          {{ runWhileText }}
        </p>
        <div
          v-for="field in FLEET_RUNNER_SETTING_FIELDS"
          :key="field.key"
          class="fleet-runner-settings__field"
        >
          <div class="fleet-runner-settings__label">
            <span>{{ field.label }}</span>
            <WSwitch
              v-if="field.kind === 'switch'"
              :model-value="Boolean(chosen[field.key])"
              :aria-label="field.label"
              on-label="On"
              off-label="Off"
              :section-tag="`${sectionTag}-${field.key}`"
              @update:model-value="(value: boolean) => choose(field.key, value)"
            />
          </div>
          <NumericStepper
            v-if="field.kind === 'number'"
            :model-value="numberOf(field.key)"
            :min="0"
            :max="FLEET_RUNNER_MIN_DISK_MAX"
            :step="1"
            :error-message="minDiskError"
            :aria-label="field.label"
            :section-tag="`${sectionTag}-${field.key}`"
            @update:model-value="(value: number | null) => choose(field.key, value ?? undefined)"
          />
          <WDropdown
            v-if="field.kind === 'one-of'"
            :model-value="chosen[field.key] === undefined ? null : String(chosen[field.key])"
            :items="field.options ?? []"
            item-title="title"
            item-value="value"
            :aria-label="field.label"
            placeholder="Not chosen yet"
            size="medium"
            :section-tag="`${sectionTag}-${field.key}`"
            @update:model-value="(value: string) => choose(field.key, value)"
          />
          <p class="fleet-runner-settings__fact">{{ field.description }}</p>
        </div>
      </div>
    </template>
  </WModal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import NumericStepper from '@/components/shared/NumericStepper.vue'

import {
  FLEET_RUNNER_MIN_DISK_MAX,
  FLEET_RUNNER_SETTING_FIELDS,
  changedMachineSettings,
  isMinDiskValid,
  runnerChoiceText,
  runnerMachineSettings,
  runnerRunWhileText
} from '@/services/claude-fleet'
import type {
  FleetRunner,
  FleetRunnerEditableSettings,
  FleetRunnerSettingKey
} from '@/services/claude-fleet'
import { Actions } from '@/constants/actions'

import WModal from '@wasabi/content-displays/WModal.vue'
import WDropdown from '@wasabi/inputs/WDropdown.vue'
import WSwitch from '@wasabi/inputs/WSwitch.vue'

const props = defineProps<{ runner: FleetRunner; ownerName: string; sectionTag: string }>()

const emit = defineEmits<{ confirm: [settings: Partial<FleetRunnerEditableSettings>] }>()

const isOpen = defineModel<boolean>({ required: true })

const current = computed(() => runnerMachineSettings(props.runner) ?? {})
const chosen = ref<Partial<FleetRunnerEditableSettings>>({ ...current.value })

const text = computed(() => runnerChoiceText('settings', props.ownerName))
const runWhileText = computed(() => runnerRunWhileText(current.value))
const changes = computed(() => changedMachineSettings(current.value, chosen.value))
const minDiskError = computed(() =>
  chosen.value.minDiskGb === undefined || isMinDiskValid(chosen.value.minDiskGb)
    ? ''
    : `A whole number from 0 to ${FLEET_RUNNER_MIN_DISK_MAX}`
)
const canSave = computed(() => Object.keys(changes.value).length > 0 && !minDiskError.value)

watch(isOpen, opened => {
  if (opened) chosen.value = { ...current.value }
})

function numberOf(key: FleetRunnerSettingKey): number | null {
  const value = chosen.value[key]
  return typeof value === 'number' ? value : null
}

function choose(key: FleetRunnerSettingKey, value: unknown) {
  chosen.value = { ...chosen.value, [key]: value }
}

function handleConfirm() {
  isOpen.value = false
  if (canSave.value) emit('confirm', changes.value)
}
</script>

<style scoped>
.fleet-runner-settings {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
}

.fleet-runner-settings__field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.fleet-runner-settings__label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.fleet-runner-settings__text,
.fleet-runner-settings__fact {
  margin: 0;
  overflow-wrap: anywhere;
}

.fleet-runner-settings__fact {
  font-size: 12px;
  color: var(--color-content-text-muted, #767676);
}
</style>
